'use client';
import React, { useState } from 'react';
import Image from 'next/image';

import Button from '@/components/ui/Button';
import { useParams, useRouter } from 'next/navigation';
import { defaultLocale } from '@/constants';

const Header = (props) => {
  const { dict } = props;

  const params = useParams();
  const router = useRouter();

  const currentLocale = params.lang || defaultLocale;

  const [isLanguageOpen, setIsLanguageOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState('');

  const toggleLanguage = () => {
    setIsLanguageOpen(!isLanguageOpen);
  };

  const languageOptions = [
    { code: 'uk', label: 'УКР' },
    { code: 'en', label: 'ENG' },
  ];

  const headerItems = [
    { label: dict.headerAboutUs, href: '#about' },
    { label: dict.headerQuality, href: '#quality' },
    { label: dict.headerContactUs, href: '#contact' },
  ];

  const currentLanguage =
    languageOptions.find((lang) => lang.code === currentLocale) ||
    languageOptions[0];

  const otherLanguages = languageOptions.filter(
    (lang) => lang.code !== currentLanguage.code
  );

  const handleLanguageChange = (langCode) => {
    if (langCode === defaultLocale) {
      router.push('/');
      return;
    }
    router.push(`/${langCode}`);
    setIsLanguageOpen(false);
  };

  const handleChangeSection = (href) => {
    setCurrentSection(href);
    const section = document.querySelector(href);

    if (section) {
      const yOffset = -100;
      const y =
        section.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <header className='sticky top-0 bg-black/90 backdrop-blur-md border-b border-white/10 py-4 px-4 z-50'>
      <div className='max-w-7xl mx-auto flex items-center justify-between'>
        {/* Logo */}
        <a
          href={currentLocale === defaultLocale ? '/' : `/${currentLocale}`}
          className='flex items-center gap-3 group'
        >
          <Image
            src='/images/logo_mini.png'
            alt='Техніка і гаджети'
            width={56}
            height={56}
            className='invert'
          />
          <div className='hidden sm:flex flex-col leading-tight'>
            <span className='text-base font-oswald font-semibold text-white tracking-wide uppercase'>
              Техніка і гаджети
            </span>
            <span className='text-xs font-montserrat text-gray-400 uppercase tracking-[0.18em]'>
              Distribution
            </span>
          </div>
        </a>

        {/* Navigation */}
        <nav className='hidden md:flex items-center space-x-8'>
          {headerItems.map((item) =>
            item.href === currentSection ? (
              <div
                key={item.href}
                className='bg-white/10 border border-white/20 rounded-lg px-7 py-3'
              >
                <span className='text-base font-montserrat font-semibold text-white'>
                  {item.label}
                </span>
              </div>
            ) : (
              <span
                key={item.href}
                onClick={() => handleChangeSection(item.href)}
                className='text-base font-montserrat font-medium text-gray-300 hover:text-accent transition-colors cursor-pointer'
              >
                {item.label}
              </span>
            )
          )}
        </nav>

        {/* Language and CTA */}
        <div className='flex items-center space-x-6'>
          <div className='relative'>
            <button
              onClick={toggleLanguage}
              className='flex items-center space-x-2 text-base font-montserrat font-medium text-white border border-white/20 rounded-lg px-3 py-2 hover:border-white/40 transition-colors'
            >
              <span>{currentLanguage.label}</span>
              <svg
                width='10'
                height='6'
                viewBox='0 0 10 6'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                className={`transition-transform ${isLanguageOpen ? 'rotate-180' : ''}`}
              >
                <path
                  d='M1 1L5 5L9 1'
                  stroke='currentColor'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>

            {isLanguageOpen && (
              <div className='absolute z-10 right-0 top-full mt-2 bg-gray-900 border border-white/10 rounded-lg py-2 min-w-[80px]'>
                {otherLanguages.map((lang) => (
                  <button
                    key={lang.code}
                    className='block w-full text-left text-white hover:text-accent px-4 py-2 transition-colors'
                    onClick={() => handleLanguageChange(lang.code)}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <Button
            className='bg-accent text-white font-montserrat font-semibold px-6 py-3 rounded-lg hover:bg-accent-dark transition-colors'
            onClick={() => handleChangeSection('#contact')}
          >
            {dict.headerButton}
          </Button>
        </div>
      </div>
    </header>
  );
};

export default Header;
